import React from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Image, rem, Text, VStack } from '@bonusly/design-system';

import DeflatedBalloon from '../../../../components/celebrations/assets/deflated-balloon.svg';
import { ModalLoadingState } from '../../../../components/celebrations/celebrations-widget/components/modal-loading-state';
import { CelebrationGroup } from '../../../../components/celebrations/types';
import { PanelCelebrationItem } from './panel-celebration-item';

interface PanelCelebrationsGroupProps {
	celebrations: CelebrationGroup;
	isLoading?: boolean;
	onClose: () => void;
}

export const PanelCelebrationsGroup = ({ celebrations, isLoading, onClose }: PanelCelebrationsGroupProps): React.ReactElement => {
	const { t } = useTranslation('celebrations');

	if (isLoading) {
		return <ModalLoadingState />;
	}

	if (celebrations.count === 0) {
		return (
			<Box px={2} alignContent="center">
				<Image height={rem(250)} margin="auto" pr={12} src={DeflatedBalloon} />
				<Text fontSize={100} fontWeight="normal" color="text.default" textAlign="center" pt={3} margin="auto" width="70%">
					{t('modal.no_celebrations')}
				</Text>
			</Box>
		);
	}

	return (
		<Box>
			{celebrations.recent.length > 0 && (
				<Box mb={4}>
					<SectionHeading>{t('modal.recent')}</SectionHeading>
					<VStack as="ul" spacing={1} align="stretch" mt={2}>
						{celebrations.recent.map((celebration) => (
							<Box as="li" key={`panel-item-${celebration.id ?? `${celebration.user_id}-${celebration.type}`}`} listStyleType="none">
								<PanelCelebrationItem celebration={celebration} onClose={onClose} />
							</Box>
						))}
					</VStack>
				</Box>
			)}
			{celebrations.upcoming.length > 0 && (
				<Box mb={4}>
					<SectionHeading>{t('modal.upcoming')}</SectionHeading>
					<VStack as="ul" spacing={1} align="stretch" mt={2}>
						{celebrations.upcoming.map((celebration) => (
							<Box as="li" key={`panel-item-${celebration.id ?? `${celebration.user_id}-${celebration.type}`}`} listStyleType="none">
								<PanelCelebrationItem celebration={celebration} onClose={onClose} />
							</Box>
						))}
					</VStack>
				</Box>
			)}
		</Box>
	);
};

const SectionHeading = ({ children }: { children: React.ReactNode }) => (
	<Text fontSize={100} fontWeight="semibold" color="text.default" lineHeight={5}>
		{children}
	</Text>
);
